import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FiAlertCircle, FiChevronDown, FiChevronRight, FiFileText, FiLoader } from 'react-icons/fi';

import Alert from '../../../components/Alert';
import { EbookSubmissionPreviewRowFragment } from '../../../core/graphql/types';
import { getDayTime } from '../../../utils/dayjs';

const DATE_FORMAT = 'D-MMM-YYYY H:mm';

export interface IPropsEbookSubmissionPreviewPane {
  rows: EbookSubmissionPreviewRowFragment[];
  isParsing?: boolean;
}

type Row = EbookSubmissionPreviewRowFragment;

function formatDate(value?: string | null): string {
  return value ? getDayTime(value, DATE_FORMAT) : '—';
}

function formatThb(value?: number | null): string {
  return value === null || value === undefined ? '—' : `฿${value.toLocaleString()}`;
}

function formatList(values: string[]): string {
  return values.length ? values.join(', ') : '—';
}

function formatTier(id?: number | null, thb?: number | null): string {
  if (id == null) return '—';
  return thb != null ? `#${id} (฿${thb.toLocaleString()})` : `#${id}`;
}

function FileLink({ url }: { url?: string | null }) {
  if (!url) return <span>—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="underline text-colorsBrandRed01Primary">
      View
    </a>
  );
}

// Error `value` is plain text for most fields (raw CSV input) but a Drive URL for the six
// file fields — render those as a clickable "View" link (matching FileLink), otherwise as-is.
function renderErrorValue(value: string) {
  if (!/^https?:\/\//.test(value)) return value;
  return (
    <a href={value} target="_blank" rel="noreferrer" className="underline text-colorsBrandRed01Primary">
      View
    </a>
  );
}

const USER_NOT_FOUND_TEXT = 'ไม่พบผู้ใช้งานในระบบ';

// Distinguishes "no username resolved because the userId doesn't exist" (an error, must stand out)
// from "username just isn't set" (normal missing data) — both would otherwise render as blank/'—'.
function getUsernameDisplay(row: Row): { text: string; isError: boolean } {
  const error = row.errors.find((error) => error.field === 'userId');
  if (error) return { text: error.value ?? USER_NOT_FOUND_TEXT, isError: true };
  return { text: row.username ?? row.userId ?? '—', isError: false };
}

interface IFieldRow {
  key: string;
  label: string;
  matchFields?: string[];
  render: (row: Row) => React.ReactNode;
}

interface IFieldGroup {
  title: string;
  fields: IFieldRow[];
}

// Grouped so the detail panel reads like the CSV template's own sections, not 40 flat cells —
// and so a field's error sits right next to its value instead of in a column 40 cells away.
const FIELD_GROUPS: IFieldGroup[] = [
  {
    title: 'Content',
    fields: [
      { key: 'username', label: 'User', matchFields: ['userId'], render: (row) => getUsernameDisplay(row).text },
      { key: 'description', label: 'Description', matchFields: ['description'], render: (row) => row.description || '—' },
      { key: 'contentWarning', label: 'Content warning', matchFields: ['triggerWarning'], render: (row) => row.contentWarningPlainText ?? '—' },
      { key: 'synopsis', label: 'Synopsis', matchFields: ['synopsis'], render: (row) => row.synopsisPlainText || '—' },
      { key: 'tagNames', label: 'Tags', matchFields: ['tagNames'], render: (row) => formatList(row.tagNames ?? []) },
      { key: 'authorNames', label: 'Authors', matchFields: ['authorNames'], render: (row) => formatList(row.authorNames) },
      { key: 'translatorNames', label: 'Translators', matchFields: ['translatorNames'], render: (row) => formatList(row.translatorNames) },
      { key: 'originalLanguage', label: 'Original language', matchFields: ['originalLanguage'], render: (row) => row.originalLanguage ?? '—' },
      { key: 'language', label: 'Language', matchFields: ['language'], render: (row) => row.language ?? '—' },
      { key: 'isbn', label: 'ISBN', matchFields: ['isbn'], render: (row) => row.isbn ?? '—' },
      { key: 'writingType', label: 'Writing type', matchFields: ['writingType'], render: (row) => row.writingType ?? '—' },
      { key: 'readingType', label: 'Reading type', matchFields: ['readingType'], render: (row) => row.readingType ?? '—' },
    ],
  },
  {
    title: 'Pricing & discount',
    fields: [
      { key: 'coverPriceThb', label: 'Cover price', matchFields: ['coverPriceThb'], render: (row) => formatThb(row.coverPriceThb) },
      { key: 'priceThb', label: 'Price', matchFields: ['priceThb'], render: (row) => (row.priceThb === 0 ? 'Free' : formatThb(row.priceThb)) },
      { key: 'applePriceTier', label: 'Apple tier', matchFields: ['applePriceTierId'], render: (row) => formatTier(row.applePriceTierId, row.applePriceTierThb) },
      { key: 'googlePriceTier', label: 'Google tier', matchFields: ['googlePriceTierId'], render: (row) => formatTier(row.googlePriceTierId, row.googlePriceTierThb) },
      {
        key: 'webDiscountPercentage',
        label: 'Discount %',
        matchFields: ['webDiscountPercentage'],
        render: (row) => (row.webDiscountPercentage != null ? `${row.webDiscountPercentage}%` : '—'),
      },
      { key: 'webDiscountThb', label: 'Discount ฿', matchFields: ['webDiscountThb'], render: (row) => formatThb(row.webDiscountThb) },
      {
        key: 'appleDiscountPriceTier',
        label: 'Apple discount tier',
        matchFields: ['appleDiscountPriceTierId'],
        render: (row) => formatTier(row.appleDiscountPriceTierId, row.appleDiscountPriceTierThb),
      },
      {
        key: 'googleDiscountPriceTier',
        label: 'Google discount tier',
        matchFields: ['googleDiscountPriceTierId'],
        render: (row) => formatTier(row.googleDiscountPriceTierId, row.googleDiscountPriceTierThb),
      },
      { key: 'discountStartedAt', label: 'Discount start', matchFields: ['discountStartedAt'], render: (row) => formatDate(row.discountStartedAt) },
      { key: 'discountEndedAt', label: 'Discount end', matchFields: ['discountEndedAt'], render: (row) => formatDate(row.discountEndedAt) },
    ],
  },
  {
    title: 'Publishing',
    fields: [
      { key: 'productSetId', label: 'Product set ID', matchFields: ['productSetId'], render: (row) => row.productSetId ?? '—' },
      { key: 'productSetTitle', label: 'Product set', matchFields: ['productSetId'], render: (row) => row.productSetTitle ?? '—' },
      { key: 'productSetVol', label: 'Product set vol', matchFields: ['productSetVol'], render: (row) => row.productSetVol ?? '—' },
      { key: 'subscriptionTitleId', label: 'Subscription title ID', matchFields: ['subscriptionTitleId'], render: (row) => row.subscriptionTitleId ?? '—' },
      { key: 'subscriptionTitleTitle', label: 'Subscription title', matchFields: ['subscriptionTitleId'], render: (row) => row.subscriptionTitleTitle ?? '—' },
      { key: 'publishedAt', label: 'Publish date', matchFields: ['publishedAt'], render: (row) => formatDate(row.publishedAt) },
    ],
  },
  {
    title: 'Files',
    fields: [
      { key: 'coverImageUrl', label: 'Cover', matchFields: ['coverImageUrl'], render: (row) => <FileLink url={row.coverImageUrl} /> },
      { key: 'bannerImageUrl', label: 'Banner', matchFields: ['bannerImageUrl'], render: (row) => <FileLink url={row.bannerImageUrl} /> },
      { key: 'samplePdfFileUrl', label: 'Sample PDF', matchFields: ['samplePdfFileUrl'], render: (row) => <FileLink url={row.samplePdfFileUrl} /> },
      { key: 'mainPdfFileUrl', label: 'Main PDF', matchFields: ['mainPdfFileUrl'], render: (row) => <FileLink url={row.mainPdfFileUrl} /> },
      { key: 'sampleEpubFileUrl', label: 'Sample EPUB', matchFields: ['sampleEpubFileUrl'], render: (row) => <FileLink url={row.sampleEpubFileUrl} /> },
      { key: 'mainEpubFileUrl', label: 'Main EPUB', matchFields: ['mainEpubFileUrl'], render: (row) => <FileLink url={row.mainEpubFileUrl} /> },
    ],
  },
];

function rowKey(row: Row, index: number): string {
  // `no` is raw admin-typed CSV input — blank/duplicated across rows, so it can't stand alone as a key.
  return `${row.no}-${index}`;
}

const EbookSubmissionPreviewPane = ({ rows, isParsing }: IPropsEbookSubmissionPreviewPane) => {
  // One-shot reveal when the manifest first appears — not re-triggered on every re-render,
  // so paging/scanning the table afterward stays instant and doesn't feel busy.
  const [revealed, setRevealed] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const hasRows = rows.length > 0;

  useEffect(() => {
    if (!hasRows) {
      setRevealed(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(raf);
  }, [hasRows]);

  // Every row starts collapsed. Also resets on a new preview result so a re-selected file doesn't
  // inherit a stale open/closed state from the previous one via a `no-index` key collision.
  useEffect(() => {
    setExpandedKeys(new Set());
  }, [rows]);

  const toggleRow = (key: string): void => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isParsing) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center w-full gap-8 text-colorsBrandWarmBlack02LabelSecondary">
        <FiLoader className="text-24 animate-spin" />
        <span>Validating file…</span>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center w-full space-y-8 text-center text-colorsBrandWarmBlack02LabelSecondary">
        <FiFileText className="text-40 opacity-40" />
        <span>Choose a CSV to see what&apos;s inside before you submit it.</span>
      </div>
    );
  }

  const problemRows = rows.filter((row) => row.errors.length > 0);
  const okCount = rows.length - problemRows.length;

  return (
    <div
      className={classNames(
        'flex flex-col flex-1 w-full space-y-12 motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out',
        revealed ? 'opacity-100 translate-y-0' : 'opacity-100 motion-safe:opacity-0 motion-safe:translate-y-4',
      )}
    >
      {problemRows.length === 0 ? (
        <div className="text-14 font-tt text-colorsBrandWarmBlack02LabelSecondary">
          <span className="font-semibold text-colorsBrandWarmBlack01LabelPrimary">{rows.length}</span> books ready to submit.
        </div>
      ) : (
        <Alert type="warning" message={`${okCount} of ${rows.length} books ready`} description={`${problemRows.length} need a closer look before you submit.`} showIcon />
      )}

      <div className="overflow-auto rounded-md border border-solid border-componentsDivider grow divide-y divide-solid divide-componentsDivider font-tt">
        <div className="sticky top-0 z-10 flex items-center gap-12 px-12 py-8 text-12 font-semibold uppercase tracking-wide text-colorsBrandWarmBlack02LabelSecondary bg-colorsBrandWarmBlack07FillTertiary">
          <span className="shrink-0 w-[20px]" />
          <span className="w-[32px] shrink-0">No.</span>
          <span className="flex-1 min-w-0">Title</span>
          <span className="hidden md:block w-[140px] shrink-0">User</span>
          <span className="hidden md:block w-[120px] shrink-0">Category</span>
          <span className="w-[80px] shrink-0">Price</span>
          <span className="hidden sm:block w-[90px] shrink-0">Status</span>
          <span className="w-[70px] shrink-0" />
        </div>
        {rows.map((row, index) => {
          const key = rowKey(row, index);
          const isOpen = expandedKeys.has(key);
          const hasIssue = row.errors.length > 0;
          const usernameDisplay = getUsernameDisplay(row);
          const titleError = row.errors.find((error) => error.field === 'title');
          const categoryError = row.errors.find((error) => error.field === 'categoryId');
          const priceError = row.errors.find((error) => error.field === 'priceThb');
          const statusError = row.errors.find((error) => error.field === 'status');

          return (
            <div key={key} className={classNames(hasIssue && 'bg-colorsBrandRed04Lightest')}>
              <button
                type="button"
                onClick={() => toggleRow(key)}
                aria-expanded={isOpen}
                className="w-full flex items-center gap-12 px-12 py-10 text-left transition-opacity hover:opacity-80"
              >
                <span className="flex shrink-0 w-[20px] justify-center text-systemGrays02LabelSecondary">{isOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
                <span className="w-[32px] shrink-0 text-12 text-colorsBrandWarmBlack02LabelSecondary">{row.no || '—'}</span>
                <span className={classNames('flex-1 min-w-0 truncate font-semibold', titleError && 'text-colorsBrandRed01Primary')}>
                  {titleError ? titleError.value ?? '—' : row.title || '—'}
                </span>
                <span
                  className={classNames(
                    'hidden md:block w-[140px] shrink-0 truncate text-12',
                    usernameDisplay.isError ? 'text-colorsBrandRed01Primary font-semibold' : 'text-colorsBrandWarmBlack02LabelSecondary',
                  )}
                >
                  {usernameDisplay.text}
                </span>
                <span
                  className={classNames(
                    'hidden md:block w-[120px] shrink-0 truncate text-12',
                    categoryError ? 'text-colorsBrandRed01Primary font-semibold' : 'text-colorsBrandWarmBlack02LabelSecondary',
                  )}
                >
                  {categoryError ? categoryError.value ?? '—' : row.categoryName ?? '—'}
                </span>
                <span className={classNames('w-[80px] shrink-0 text-12', priceError ? 'text-colorsBrandRed01Primary font-semibold' : 'text-colorsBrandWarmBlack02LabelSecondary')}>
                  {priceError ? priceError.value ?? '—' : row.priceThb === 0 ? 'Free' : formatThb(row.priceThb)}
                </span>
                <span
                  className={classNames(
                    'hidden sm:block w-[90px] shrink-0 text-12',
                    statusError ? 'text-colorsBrandRed01Primary font-semibold' : 'text-colorsBrandWarmBlack02LabelSecondary',
                  )}
                >
                  {statusError ? statusError.value ?? '—' : row.status ? row.status.toUpperCase() : '—'}
                </span>
                <span className="w-[70px] shrink-0">
                  {hasIssue ? (
                    <span className="badge badge-error badge-sm">
                      {row.errors.length} issue{row.errors.length > 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="badge badge-success badge-sm">Ready</span>
                  )}
                </span>
              </button>

              {isOpen && (
                <div className="px-12 pb-16 pl-[52px] space-y-16">
                  {hasIssue && (
                    <div className="rounded-8 bg-colorsBrandWarmBlack07FillTertiary px-12 py-8">
                      <div className="flex items-center gap-6 text-14 font-semibold uppercase tracking-wide text-colorsBrandRed01Primary">
                        <FiAlertCircle className="shrink-0" />
                        <span>
                          {row.errors.length} issue{row.errors.length > 1 ? 's' : ''} to fix
                        </span>
                      </div>
                      <ul className="mt-4 divide-y divide-solid divide-componentsDivider">
                        {row.errors.map((error, i) => (
                          <li key={i} className="flex items-center gap-8 py-4 text-14">
                            <span className="shrink-0 rounded-4 bg-colorsBrandRed08FillQuaternary px-6 py-1 font-semibold text-colorsBrandRed01Primary">{error.field}</span>
                            <span className="text-colorsBrandWarmBlack01LabelPrimary">{error.message}</span>
                            {error.value != null && (
                              <span className="text-colorsBrandWarmBlack02LabelSecondary">
                                (input: {/^https?:\/\//.test(error.value) ? renderErrorValue(error.value) : `"${error.value}"`})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {FIELD_GROUPS.map((group) => (
                    <div key={group.title}>
                      <div className="text-12 uppercase tracking-wide text-colorsBrandWarmBlack02LabelSecondary mb-8">{group.title}</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-16 gap-y-8">
                        {group.fields.map((field) => {
                          const fieldError = row.errors.find((error) => field.matchFields?.includes(error.field));
                          return (
                            <div key={field.key} className={classNames(fieldError && 'border-l-2 border-solid border-colorsBrandRed01Primary pl-8 -ml-8')}>
                              <div className="text-12 text-colorsBrandWarmBlack02LabelSecondary">{field.label}</div>
                              <div className={classNames('text-14', fieldError && 'text-colorsBrandRed01Primary font-semibold')}>
                                {fieldError?.value != null ? renderErrorValue(fieldError.value) : field.render(row)}
                              </div>
                              {fieldError && <div className="text-12 text-colorsBrandRed01Primary mt-1">{fieldError.message}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EbookSubmissionPreviewPane;
